import React, { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useState } from "react";

import { Box, BoxOwner } from "../../../../../../dtos/box";
import { Customer } from "../../../../../../dtos/customer";
import { BoxFeatureSetup } from "../../types";
import { OwnerOption } from "../interfaces";
import { useOwnerOptions } from "../useOwnerOptions";
import { useAddOwner } from "./useAddOwner";

export interface IAddOwnerFlowContext {
    box: Box;
    owners: BoxOwner[];
    boxFeatures: BoxFeatureSetup;
    onBoxOwnerAdded: () => void;
    addingOwner: boolean;
    openAddOwnerDialog: boolean;
    setOpenAddOwnerDialog: Dispatch<SetStateAction<boolean>>;
    openNewCustomerDialog: boolean;
    setOpenNewCustomerDialog: Dispatch<SetStateAction<boolean>>;
    openNewLeaseDialog: boolean;
    setOpenNewLeaseDialog: Dispatch<SetStateAction<boolean>>;
    openUpdateLeaseDialog: boolean;
    setOpenUpdateLeaseDialog: Dispatch<SetStateAction<boolean>>;
    showOrgCustomers: boolean;
    setShowOrgCustomers: (value: boolean) => void;
    potentialOwnerOptions: OwnerOption[];
    loadingOwnerOptions: boolean;
    selectedOwnerId: number | null;
    setSelectedOwnerId: Dispatch<SetStateAction<number | null>>;
    handleNewCustomer: (customer: Customer) => void;
    handleAddOwner: () => void;
    onOwnerAdded: () => void;
}

export const AddOwnerFlowContext = createContext<IAddOwnerFlowContext | null>(null);

interface ProviderProps {
    box: Box;
    owners: BoxOwner[];
    boxFeatures: BoxFeatureSetup;
    onBoxOwnerAdded: () => void;
}

export function AddOwnerFlowContextProvider({
    box,
    owners,
    boxFeatures,
    onBoxOwnerAdded,
    children,
}: React.PropsWithChildren<ProviderProps>) {
    const [openAddOwnerDialog, setOpenAddOwnerDialog] = useState(false);
    const [openNewCustomerDialog, setOpenNewCustomerDialog] = useState(false);
    const [openNewLeaseDialog, setOpenNewLeaseDialog] = useState(false);
    const [openUpdateLeaseDialog, setOpenUpdateLeaseDialog] = useState(false);

    const [selectedOwnerId, setSelectedOwnerId] = useState<number | null>(null);

    const { addOwner, loading: addingOwner } = useAddOwner(box.id);

    const {
        loading: loadingOwnerOptions,
        load: loadOwnerOptions,
        potentialOwnerOptions,
        showOrgCustomers,
        setShowOrgCustomers,
    } = useOwnerOptions(box, owners);

    useEffect(() => {
        setSelectedOwnerId(null);
    }, [box.isBankBox]);

    const handleNewCustomer = useCallback((customer: Customer) => {
        loadOwnerOptions();
        setSelectedOwnerId(customer.id);
        setOpenNewCustomerDialog(false);
    }, [loadOwnerOptions]);

    const isFirstOwner = owners.length === 0;
    const isLeaseExpired = Boolean(box.isExpired);

    const onOwnerAdded = useCallback(() => {
        onBoxOwnerAdded();
        setSelectedOwnerId(null);
        setOpenAddOwnerDialog(false);
        setShowOrgCustomers(false);
    }, [
        onBoxOwnerAdded,
    ]);

    const handleAddOwner = useCallback(() => {
        if (!selectedOwnerId) return;

        if (isFirstOwner) {
            setOpenNewLeaseDialog(true);
            return;
        }

        if (isLeaseExpired) {
            setOpenUpdateLeaseDialog(true);
            return;
        }

        addOwner(selectedOwnerId, null, false, onOwnerAdded);
    }, [
        addOwner,
        selectedOwnerId,
        isFirstOwner,
        isLeaseExpired,
        onOwnerAdded,
    ]);

    return (
        <AddOwnerFlowContext.Provider
            value={{
                box,
                owners,
                boxFeatures,
                onBoxOwnerAdded,
                addingOwner,
                openAddOwnerDialog,
                setOpenAddOwnerDialog,
                openNewCustomerDialog,
                setOpenNewCustomerDialog,
                openNewLeaseDialog,
                setOpenNewLeaseDialog,
                openUpdateLeaseDialog,
                setOpenUpdateLeaseDialog,
                showOrgCustomers,
                setShowOrgCustomers,
                potentialOwnerOptions,
                loadingOwnerOptions,
                selectedOwnerId,
                setSelectedOwnerId,
                handleNewCustomer,
                handleAddOwner,
                onOwnerAdded,
            }}
        >
            {children}
        </AddOwnerFlowContext.Provider>
    );
}

export function useAddOwnerFlow() {
    const value = useContext(AddOwnerFlowContext);

    if (!value) {
        throw new Error("AddOwnerFlowContext consumed outside of AddOwnerFlowContextProvider scope")
    }

    return value;
}
